import { useState } from "react";
import { useHistory } from "react-router-dom";
import { withTranslation, useTranslation } from "react-i18next";
import { usePreCheckInContext } from "./contexts/PreCheckInContext";
import { get, isEmpty } from "lodash";
import FloatInput from "./FloatInput";
import validator from "validator";
import ReservationService from "../../backend/ReservationService";
import { ClipLoaderIcon } from "../../components/LoadingIcon";

const AddGuest = () => {
  const initialFormState = {
    adultFirstName: "",
    adultLastName: "",
    adultEmail: "",
  };
  const {
    propertyUid,
    bookingNumber,
    language,
    lastName,
    userEmail,
    func: { useFormInput, updateGuests, updateErrorPage, setAccessTokenData },
    data: { presets, accessToken, booking, guests, configuration },
    components: { PreCheckInHeader, PreCheckInContainer },
  } = usePreCheckInContext();

  const history = useHistory();
  const { t } = useTranslation();
  const [formData, handleFormChange] = useFormInput(initialFormState);
  const [validateForm, setValidateForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const addMyChildPage = () => {
    let path = `/pre-checkin/${propertyUid}/guest-list-management/${bookingNumber}/${encodeURIComponent(
      lastName
    )}/add-my-child?language=${language}`;
    if (!isEmpty(userEmail)) path += `&email=${encodeURIComponent(userEmail)}`;
    history.push(path);
  };
  const addAdultPage = () => {
    let path = `/pre-checkin/${propertyUid}/guest-list-management/${bookingNumber}/${encodeURIComponent(
      lastName
    )}/add-adult?language=${language}`;
    if (!isEmpty(userEmail)) path += `&email=${encodeURIComponent(userEmail)}`;
    history.push(path);
  };
  const addAdultWithChildPage = () => {
    let path = `/pre-checkin/${propertyUid}/guest-list-management/${bookingNumber}/${encodeURIComponent(
      lastName
    )}/add-adult-with-child?language=${language}`;
    if (!isEmpty(userEmail)) path += `&email=${encodeURIComponent(userEmail)}`;
    history.push(path);
  };

  const prepareData = () => {
    const reservationId = get(booking, "resv_id");
    const data = {
      reservation_id: reservationId,
      property_uid: propertyUid,
      guest_details: {
        first_name: formData.adultFirstName,
        last_name: formData.adultLastName,
        email: formData.adultEmail,
        children: [],
      },
      reservation: {
        property_uid: propertyUid,
        reservation_id: reservationId,
      },
    };
    return data;
  };

  const addBtn = () => {
    setSubmitted(true);
    const validate =
      !isEmpty(formData.adultFirstName) &&
      !isEmpty(formData.adultLastName) &&
      !isEmpty(formData.adultEmail) &&
      validator.isEmail(formData.adultEmail) &&
      !isEmailDuplicate(formData.adultEmail);

    if (validate) {
      if (presets.demoMode) {
        keepGuestDataAndMove(prepareData());
      } else {
        requestCreateAdditionalGuest(prepareData());
      }
    }
    setValidateForm(validate);
  };

  const keepGuestDataAndMove = (newChildrenData) => {
    const guestId = Math.floor(Math.random() * (9999 - 1000 + 1)) + 1000;
    let guestData = {
      ...newChildrenData.guest_details,
      firstName: newChildrenData.guest_details.first_name,
      lastName: newChildrenData.guest_details.last_name,
      guestId,
      guest_id: guestId,
    };
    updateAdult(guestData);
    goBack();
  };

  const updateAdult = (guestData) => {
    if (isEmpty(guestData)) return;

    guests.newGuest(guestData);
    updateGuests(guests);
  };

  const goBack = () => {
    let path = `/pre-checkin/${propertyUid}/guest-list-management/${bookingNumber}/${encodeURIComponent(
      lastName
    )}?language=${language}&token=${accessToken}`;
    if (!isEmpty(userEmail)) path += `&email=${encodeURIComponent(userEmail)}`;
    history.push(path);
  };

  const requestCreateAdditionalGuest = async (newChildrenData) => {
    setLoading(true);
    window.scrollTo(0, 0);
    await ReservationService.createAdditionalGuest(
      accessToken,
      propertyUid,
      newChildrenData
    )
      .then((response) => {
        updateAdult(get(response.data, "guest_details"));
        goBack();
      })
      .catch((error) => {
        if (get(error.response, "status") === 401) {
          setAccessTokenData();
        }
        console.error(error);
        updateErrorPage(true);
      });
  };

  const isEmailDuplicate = (email) => {
    return guests.allGuests().some((guest) => guest?.email === email);
  };

  const getEmailError = (email) => {
    if (isEmpty(email)) {
      return t(
        "PLEASE_FILL_OUT_REQUIRED",
        "Please fill all the required fields"
      );
    } else if (!validator.isEmail(email)) {
      return t("INVALID_EMAIL_GUEST", "Please enter a valid email");
    } else if (isEmailDuplicate(email)) {
      return t(
        "ERROR_EMAIL_DUPLICATED_GUEST",
        "Error: The email is duplicated with another guest"
      );
    }
    return null;
  };

  return (
    <>
      <PreCheckInContainer containerName="add-guest">
        <PreCheckInHeader
          title={t("ADD_GUEST")}
          headerLink
        />
        {guests && booking && (
          <>
            <section
              id="content"
              className="relative flex flex-col px-9 w-full flex-1 md:flex-0 pt-6">
              <div className="flex flex-col h-full">
                <div className="flex mb-10 w-full h-full">
                  <div className="font-normal m-auto h-full">
                    <p
                      className="mb-12 type-normal large"
                      style={{ color: presets.titleColor }}>
                      {t("ADD_GUEST_DESCRIPTION")}
                    </p>
                    {configuration.checkPreCheckInInterfaceTypeIsOpera ? (
                      <div className="flex flex-col">
                        <div className="flex mb-10 w-full">
                          <div className="font-normal w-full">
                            <div className="flex flex-col">
                              <div className="pt-5 mb-24 relative">
                                <FloatInput
                                  id="adultFirstName"
                                  name="adultFirstName"
                                  label={t("FIRSTNAME")}
                                  span={`*${t("REQUIRED")}`}
                                  classNameLabel="mb-6 type-title small"
                                  classNameSpan="text-gray-400 type-regular small pl-2"
                                  classNameInput="capitalize border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                                  onChange={handleFormChange}
                                  value={formData.adultFirstName}
                                  labelColor={presets.backgroundColorTopBar}
                                  borderColor={presets.backgroundColorTopBar}
                                />
                                {!validateForm &&
                                  submitted &&
                                  isEmpty(formData.adultFirstName) && (
                                    <p className="text-14 text-red-500 mt-5">
                                      {t(
                                        "PLEASE_FILL_OUT_REQUIRED",
                                        "Please fill all the required fields"
                                      )}
                                    </p>
                                  )}
                              </div>

                              <div className="mb-24 relative">
                                <FloatInput
                                  id="adultLastName"
                                  name="adultLastName"
                                  label={t("LAST_NAME")}
                                  span={`*${t("REQUIRED")}`}
                                  classNameLabel="mb-6 type-title small"
                                  classNameSpan="text-gray-400 type-regular small pl-2"
                                  classNameInput="capitalize border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                                  onChange={handleFormChange}
                                  value={formData.adultLastName}
                                  labelColor={presets.backgroundColorTopBar}
                                  borderColor={presets.backgroundColorTopBar}
                                />
                                {!validateForm &&
                                  submitted &&
                                  isEmpty(formData.adultLastName) && (
                                    <p className="text-14 text-red-500 mt-5">
                                      {t(
                                        "PLEASE_FILL_OUT_REQUIRED",
                                        "Please fill all the required fields"
                                      )}
                                    </p>
                                  )}
                              </div>

                              <div className="mb-24 relative">
                                <FloatInput
                                  id="adultEmail"
                                  name="adultEmail"
                                  label={t("EMAIL")}
                                  span={`*${t("REQUIRED")}`}
                                  classNameLabel="mb-6 type-title small"
                                  classNameSpan="text-gray-400 type-regular small pl-2"
                                  classNameInput="border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                                  onChange={handleFormChange}
                                  value={formData.adultEmail}
                                  labelColor={presets.backgroundColorTopBar}
                                  borderColor={presets.backgroundColorTopBar}
                                />
                                {!validateForm && submitted && (
                                  <>
                                    <p className="text-14 text-red-500 mt-5">
                                      {getEmailError(formData.adultEmail)}
                                    </p>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <button
                          className={`app-button-ipad type-title medium ${presets.buttonStyleName}`}
                          style={{
                            backgroundColor: presets.backgroundColorTopBar,
                            color: presets.appHomeTextCallToAction,
                          }}
                          onClick={() => addBtn()}>
                          {t("SEND_INVITE")}
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col h-full justify-between md:justify-start pb-72 md:pb-10">
                        <div className="mb-12 text-center">
                          <button
                            className={`bg-white border rounded-full mx-auto px-10 py-5 type-title normal mb-4 w-80 capitalize ${presets.buttonStyleName}`}
                            style={{
                              backgroundColor: presets.backgroundColorTopBar,
                              color: presets.appHomeTextDetailsPrecheckin,
                            }}
                            onClick={() => addMyChildPage()}>
                            {t("MY_CHILD")}
                          </button>
                          <p
                            className="mb-2 type-regular medium"
                            style={{ color: presets.subtitleColor }}>
                            {t("NOTE_CHILD")}
                          </p>
                        </div>

                        <div className="mb-12 text-center">
                          <button
                            className={`bg-white border rounded-full mx-auto px-10 py-5 type-title normal mb-4 w-80 capitalize ${presets.buttonStyleName}`}
                            style={{
                              backgroundColor: presets.backgroundColorTopBar,
                              color: presets.appHomeTextDetailsPrecheckin,
                            }}
                            onClick={() => addAdultPage()}>
                            {t("ADULT")}
                          </button>
                          <p
                            className="mb-2 type-regular medium"
                            style={{ color: presets.subtitleColor }}>
                            {t("NOTE_ADULT")}
                          </p>
                        </div>

                        {booking.totalGuests - guests.numberOfGuests() > 1 && (
                          <div className="mb-6 text-center">
                            <button
                              className={`bg-white border rounded-full mx-auto px-10 py-5 type-title normal mb-4 w-80 capitalize ${presets.buttonStyleName}`}
                              style={{
                                backgroundColor: presets.backgroundColorTopBar,
                                color: presets.appHomeTextDetailsPrecheckin,
                              }}
                              onClick={() => addAdultWithChildPage()}>
                              {t("ADULT_WITH_CHILD")}
                            </button>
                            <p
                              className="mb-2 type-regular medium"
                              style={{ color: presets.subtitleColor }}>
                              {t("NOTE_ADULT_WITH_CHILD")}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
            {configuration.checkPreCheckInInterfaceTypeIsOpera && (
              <button
                className="app-button-mobile type-title medium "
                style={{
                  backgroundColor: presets.backgroundColorTopBar,
                  color: presets.appHomeTextCallToAction,
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                }}
                onClick={() => addBtn()}>
                {t("SEND_INVITE")}
              </button>
            )}
            {loading && (
              <div className="fixed inset-0 flex justify-center items-center bg-gray-500 opacity-50 z-50">
                <ClipLoaderIcon
                  loadingState={loading}
                  size={30}
                  color={presets.backgroundColorTopBar}
                />
              </div>
            )}
          </>
        )}
      </PreCheckInContainer>
    </>
  );
};

export default withTranslation()(AddGuest);
